import {
    findProductVariant,
    getLineUnitPrice,
    normalizeOptionVariants,
    resolveUnitPrice,
} from '../pages/CreateOrder/createOrderPricing';

/** Minimum characters before calling product search APIs. */
export const ORDER_PRODUCT_SEARCH_MIN_LENGTH = 3;

export const getOrderProductSearchMinLength = (term) => {
    const t = String(term ?? '').trim();
    if (/^\d+$/.test(t)) return 2;
    return ORDER_PRODUCT_SEARCH_MIN_LENGTH;
};

export const extractProductSearchResults = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.products)) return payload.products;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

export const productMatchesSearchTerm = (product, term) => {
    const q = String(term ?? '').trim().toLowerCase();
    if (!q || !product) return false;

    const name = String(product.name ?? '').toLowerCase();
    const sku = String(product.sku ?? '').toLowerCase();
    if (name.includes(q) || sku.includes(q)) return true;

    const variants = product.product_variants || product.productVariants || [];
    return variants.some((variant) => {
        const variantSku = String(variant?.sku ?? '').toLowerCase();
        return variantSku.includes(q);
    });
};

export const formatOrderProductSearchLabel = (product, searchTerm = '') => {
    if (!product) return '';
    const name = product.name || '';
    const term = String(searchTerm ?? '').trim().toLowerCase();

    const variants = product.product_variants || product.productVariants || [];
    const matchingVariant = term
        ? variants.find((variant) =>
              String(variant?.sku ?? '')
                  .toLowerCase()
                  .includes(term)
          )
        : null;

    if (matchingVariant?.sku) {
        return `${name} — SKU variante: ${matchingVariant.sku}`;
    }

    const sku = product.sku != null ? String(product.sku).trim() : '';
    return sku ? `${name} — SKU: ${sku}` : name;
};

/** Server already filters by name/SKU; disable Autocomplete client-side filtering. */
export const orderProductSearchFilterOptions = (options) => options;

export const resolveSearchProductUnitPrice = (product, searchTerm = '') => {
    if (!product) return 0;

    const term = String(searchTerm ?? '').trim().toLowerCase();
    const variants = product.product_variants || product.productVariants || [];

    if (term && variants.length) {
        const variantMatch = variants.find((variant) => {
            const variantSku = String(variant?.sku ?? '').toLowerCase();
            return variantSku.includes(term) || variantSku === term;
        });
        if (variantMatch) {
            const variantPrice = variantMatch.discount_price ?? variantMatch.price;
            if (variantPrice != null && Number(variantPrice) > 0) {
                return Number(variantPrice);
            }
        }
    }

    const discountPrice = product.discount_price ?? product.discountPrice;
    if (discountPrice != null && Number(discountPrice) > 0) {
        return Number(discountPrice);
    }
    if (product.price != null && Number(product.price) > 0) {
        return Number(product.price);
    }

    const firstVariant = variants[0];
    if (firstVariant) {
        const variantPrice = firstVariant.discount_price ?? firstVariant.price;
        if (variantPrice != null && Number(variantPrice) > 0) {
            return Number(variantPrice);
        }
    }

    return 0;
};

/** Prefer the first array that actually has items (RHF may replace catalog arrays with []). */
export const pickNonEmptyArray = (...candidates) => {
    for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length > 0) {
            return candidate;
        }
    }
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }
    return [];
};

/** Option groups (Color, Size, etc.) for order line variant selectors. */
export const getOrderLineOptionGroups = (line) => {
    const groups = pickNonEmptyArray(
        line?.optionsProduct,
        line?.product?.product_options,
        line?.product?.productOptions
    );
    return Array.isArray(groups) ? groups : [];
};

export const orderLineHasVariantOptions = (line) => getOrderLineOptionGroups(line).length > 0;

export const getOrderLineCatalogProduct = (line) => {
    if (!line) return null;
    return {
        id: line.product_id ?? line.product?.id,
        price: line.product?.price,
        discount_price: line.product?.discount_price ?? line.product?.discountPrice,
        product_variants: pickNonEmptyArray(
            line.variantsProduct,
            line.product?.product_variants,
            line.product?.productVariants
        ),
        product_options: pickNonEmptyArray(
            line.optionsProduct,
            line.product?.product_options,
            line.product?.productOptions
        ),
    };
};

export const getSelectedVariantOptions = (line) =>
    normalizeOptionVariants(line?.variants ?? line?.optionVariants);

/** Unit price from catalog product + selected option labels (x12, x36, etc.). */
export const resolveUnitPriceFromCatalogProduct = (catalogProduct, selectedVariants) => {
    if (!catalogProduct) return null;
    const selected = normalizeOptionVariants(selectedVariants);
    const variants = catalogProduct.product_variants ?? catalogProduct.productVariants ?? [];
    if (!variants.length || !selected.length) return null;

    const catalog = {
        id: catalogProduct.id,
        price: catalogProduct.price,
        discount_price: catalogProduct.discount_price ?? catalogProduct.discountPrice,
        product_variants: variants,
    };
    const { unitPrice, discountPrice } = resolveUnitPrice(catalog, selected);
    return getLineUnitPrice(unitPrice, discountPrice);
};

/** Preserve catalog fields when react-hook-form watch returns partial row data. */
export const mergeOrderLineRow = (baseRow, watchedRow) => {
    const base = baseRow ?? {};
    const watched = watchedRow ?? {};
    return {
        ...base,
        ...watched,
        variantsProduct: pickNonEmptyArray(watched.variantsProduct, base.variantsProduct),
        optionsProduct: pickNonEmptyArray(watched.optionsProduct, base.optionsProduct),
        variants: (() => {
            const watchedVariants = normalizeOptionVariants(watched.variants);
            const baseVariants = normalizeOptionVariants(base.variants);
            return watchedVariants.length ? watchedVariants : baseVariants;
        })(),
        product: {
            ...(base.product ?? {}),
            ...(watched.product ?? {}),
            product_options: pickNonEmptyArray(
                watched.product?.product_options,
                watched.product?.productOptions,
                base.product?.product_options,
                base.product?.productOptions,
                base.optionsProduct,
                watched.optionsProduct
            ),
            product_variants: pickNonEmptyArray(
                watched.product?.product_variants,
                watched.product?.productVariants,
                base.product?.product_variants,
                base.product?.productVariants,
                base.variantsProduct,
                watched.variantsProduct
            ),
        },
        price: watched.price != null && watched.price !== '' ? watched.price : base.price,
    };
};

export const enrichOrderLineWithCatalog = (line, catalogProduct) => {
    if (!line || !catalogProduct) return line ?? {};
    const variants = catalogProduct.product_variants ?? catalogProduct.productVariants ?? [];
    const options = catalogProduct.product_options ?? catalogProduct.productOptions ?? [];
    return {
        ...line,
        variantsProduct: pickNonEmptyArray(line.variantsProduct, variants),
        optionsProduct: pickNonEmptyArray(line.optionsProduct, options),
        product: {
            ...(line.product ?? {}),
            product_variants: pickNonEmptyArray(
                line.product?.product_variants,
                line.product?.productVariants,
                line.variantsProduct,
                variants
            ),
            product_options: pickNonEmptyArray(
                line.product?.product_options,
                line.product?.productOptions,
                line.optionsProduct,
                options
            ),
        },
    };
};

export const getDefaultVariantSelections = (line) => {
    const fromForm = getSelectedVariantOptions(line);
    if (fromForm.length) return fromForm;

    const fromOrderItem = line?.order_item_option
        ?.map((opt) => opt?.product_option_value?.value)
        .filter(Boolean);
    if (fromOrderItem?.length) return fromOrderItem;

    return getOrderLineOptionGroups(line)
        .map((group) => group?.options?.[0]?.name ?? group?.options?.[0]?.value)
        .filter(Boolean);
};

/** Unit price from selected variant options, else stored line / catalog price. */
export const resolveOrderLineUnitPrice = (line) => {
    const catalog = getOrderLineCatalogProduct(line);
    const selected = getSelectedVariantOptions(line);
    if (catalog?.product_variants?.length && selected.length) {
        const { unitPrice, discountPrice } = resolveUnitPrice(catalog, selected);
        return getLineUnitPrice(unitPrice, discountPrice);
    }
    if (catalog?.product_variants?.length && line?.product_variant_id) {
        const matched = catalog.product_variants.find(
            (variant) => String(variant?.id) === String(line.product_variant_id)
        );
        const fromVariant = getVariantUnitPrice(matched);
        if (fromVariant != null) {
            return fromVariant;
        }
    }
    return getOrderLineUnitPrice(line);
};

const nonEmptySku = (value) => String(value ?? '').trim();

/** SKU of the purchased variant when the line has one, otherwise the product SKU. */
export const getOrderLineSku = (row) => {
    if (!row) return '';

    const catalog = getOrderLineCatalogProduct(row);
    const selected = getSelectedVariantOptions(row);
    if (catalog && selected.length) {
        const fromOptions = nonEmptySku(findProductVariant(catalog, selected)?.sku);
        if (fromOptions) return fromOptions;
    }

    const direct = nonEmptySku(row.product_variant?.sku ?? row.productVariant?.sku);
    if (direct) return direct;

    const variantId = row.product_variant_id ?? row.productVariantId;
    const variants = pickNonEmptyArray(
        row.variantsProduct,
        catalog?.product_variants,
        catalog?.productVariants,
        row.product?.product_variants,
        row.product?.productVariants
    );

    if (variantId && variants.length) {
        const match = variants.find((variant) => String(variant?.id) === String(variantId));
        const fromId = nonEmptySku(match?.sku);
        if (fromId) return fromId;
    }

    return nonEmptySku(row.product?.sku ?? row.sku);
};

export { findProductVariant };

export const resolveOrderLineOptionIds = (line) => {
    const selected = getSelectedVariantOptions(line);
    const groups = line?.optionsProduct ?? line?.product?.product_options ?? [];

    if (selected.length && Array.isArray(groups) && groups.length) {
        const ids = [];
        groups.forEach((group, groupIndex) => {
            const selectedValue = selected[groupIndex] ?? selected[selected.length - 1];
            const match = group?.options?.find((opt) => {
                const label = String(opt?.name ?? opt?.value ?? '').toLowerCase();
                const needle = String(selectedValue ?? '').toLowerCase();
                return label === needle;
            });
            if (match?.id != null) {
                ids.push(match.id);
            }
        });
        if (ids.length) return ids;
    }

    return (
        line?.order_item_option
            ?.map((opt) => opt?.product_option_value_id)
            .filter((id) => id != null) ?? []
    );
};

export const normalizeOrderLineForSave = (line, catalogProduct) => {
    const enriched = enrichOrderLineWithCatalog(line, catalogProduct);
    const catalog = getOrderLineCatalogProduct(enriched);
    const variantsSelected = getSelectedVariantOptions(enriched);
    const matched = findProductVariant(catalog, variantsSelected);

    return {
        options: resolveOrderLineOptionIds(enriched),
        product_id: enriched?.product_id ?? enriched?.product?.id,
        product_variant_id: matched?.id ?? enriched?.product_variant_id ?? 0,
        quantity: parseInt(enriched?.quantity, 10) || 0,
        price: (() => {
            if (matched) {
                const fromVariant = getVariantUnitPrice(matched);
                if (fromVariant != null) {
                    return fromVariant;
                }
            }
            return resolveOrderLineUnitPrice(enriched);
        })(),
    };
};

/** Matches order line shape expected by OrderProductsTableToEdit (nested `product`). */
export const buildOrderLineFromSearchProduct = (product, searchTerm = '') => {
    const productOptions = product.product_options ?? product.productOptions ?? [];
    const productVariants = product.product_variants ?? product.productVariants ?? [];
    const term = String(searchTerm ?? '').trim().toLowerCase();

    let defaultVariants = productOptions
        .map((group) => group?.options?.[0]?.name ?? group?.options?.[0]?.value)
        .filter(Boolean);

    if (term && productVariants.length) {
        const skuMatch = productVariants.find((variant) => {
            const variantSku = String(variant?.sku ?? '').toLowerCase();
            return variantSku.includes(term) || variantSku === term;
        });
        if (skuMatch?.options?.length) {
            defaultVariants = skuMatch.options
                .map((option) =>
                    typeof option === 'object' && option != null
                        ? option.name ?? option.value
                        : option
                )
                .filter(Boolean);
        }
    }

    const catalogProduct = {
        price: product.price,
        discount_price: product.discount_price ?? product.discountPrice,
        product_variants: productVariants,
    };
    const { unitPrice, discountPrice } =
        defaultVariants.length && productVariants.length
            ? resolveUnitPrice(catalogProduct, defaultVariants)
            : {
                  unitPrice: resolveSearchProductUnitPrice(product, searchTerm),
                  discountPrice: null,
              };
    const linePrice = getLineUnitPrice(unitPrice, discountPrice);
    const discountRaw = product.discount_price ?? product.discountPrice;
    const discountPriceStored =
        discountPrice != null && discountPrice > 0
            ? discountPrice
            : discountRaw != null && Number(discountRaw) > 0
              ? Number(discountRaw)
              : linePrice;

    return {
        product_id: product.id,
        name: product.name,
        product: {
            id: product.id,
            name: product.name,
            sku: product.sku,
            price: unitPrice,
            discount_price: discountPriceStored,
            product_options: productOptions,
            product_variants: productVariants,
        },
        optionsProduct: productOptions,
        variantsProduct: productVariants,
        variants: defaultVariants,
        sku: product.sku,
        quantity: 1,
        price: linePrice,
        total: linePrice,
    };
};

export const getVariantUnitPrice = (variant) => {
    if (!variant) return null;
    const discount = variant.discount_price ?? variant.discountPrice;
    if (discount != null && !Number.isNaN(Number(discount)) && Number(discount) > 0) {
        return Number(discount);
    }
    const variantPrice = Number(variant.price);
    return Number.isNaN(variantPrice) ? null : variantPrice;
};

const orderLineHasVariant = (row) =>
    (row.product_variant_id != null && row.product_variant_id !== 0) ||
    row.product_variant != null ||
    row.productVariant != null;

/** Unit price for an order line: variant price when present, else stored line price, else product price. */
export const getOrderLineUnitPrice = (row) => {
    if (!row) return 0;

    if (orderLineHasVariant(row)) {
        const fromLine = getVariantUnitPrice(row.product_variant ?? row.productVariant);
        if (fromLine != null) {
            return fromLine;
        }

        const variantId = row.product_variant_id ?? row.productVariantId;
        const variants = pickNonEmptyArray(
            row.variantsProduct,
            row.product?.product_variants,
            row.product?.productVariants
        );
        if (variantId && variants.length) {
            const match = variants.find((variant) => String(variant?.id) === String(variantId));
            const fromCatalog = getVariantUnitPrice(match);
            if (fromCatalog != null) {
                return fromCatalog;
            }
        }
    }

    if (row.price != null && row.price !== '') {
        const stored = Number(row.price);
        if (!Number.isNaN(stored)) {
            return stored;
        }
    }

    const discount = row.product?.discount_price ?? row.product?.discountPrice;
    if (discount != null && Number(discount) > 0) {
        return Number(discount);
    }
    const productPrice = Number(row.product?.price);
    return !Number.isNaN(productPrice) && productPrice > 0 ? productPrice : 0;
};
